import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { getLotData, updateLot } from '../../services/Lot/lotService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import InputField from '../Utils/InputField';
import PersonInput from '../Utils/PersonInput';
import ProductInput from '../Utils/ProductInput';
import HarvestInput from '../Utils/HarvestInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import CurrencyInput from '../Utils/CurrencyInput';
import DateInput from '../Utils/DateInput';
import { convertDateToISO } from '../Utils/utils';

const EditLotModal = ({ show, handleClose, lot, refreshLots }) => {
  const { register, handleSubmit, setValue, control, formState: { errors }, reset, trigger, watch } = useForm();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchLotData = async () => {
      try {
        if (lot?.id) {
          const data = await getLotData(lot.id);
          setValue('oldId', data?.oldId);
          setValue('description', data.description);
          setValue('creditValue', data.creditValue.toFixed(2));
          setValue('deliveredValue', data.deliveredValue.toFixed(2));
          setValue('balance', data.balance.toFixed(2));
          setValue('contractAt', data.contractAt);
          setValue('observations', data.observations);
          setValue('personId', { id: data.person?.id, name: data.person?.name });
          setValue('productId',{ id: data.product?.id, description: data.product?.description });
          setValue('harvestId',{ id: data.harvest?.id, description: data.harvest?.description });
          setValue('farmId', { id: data.farm?.id, name: data.farm?.name });
          setValue('siloId', { id: data.silo?.id, name: data.silo?.name });
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados do lote.');
        setShowErrorModal(true);
      }
    };

    if (show && lot) {
      fetchLotData();
    } else if (!show) {
      reset();
    }
  }, [show, lot, setValue, reset]);

  const onSubmit = async (data) => {
    const lotData = {
      ...data,
      personId: data.personId?.id,
      productId: data.productId?.id,
      harvestId: data.harvestId?.id,
      farmId: data.farmId?.id,
      siloId: data.siloId?.id,
      contractAt: data.contractAt.includes('/') ? convertDateToISO(data.contractAt) : data.contractAt,
    };

    try {
      await updateLot(lot.id, lotData);
      setSuccessMessage('Lote atualizado com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar lote.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshLots();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Lote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <InputField
                  label="Descrição"
                  name="description"
                  placeholder="Digite a descrição"
                  register={register}
                  errors={errors}
                  rules={{ required: 'Descrição é obrigatória' }}
                />
              </Col>
              <Col md={3}>
                <CurrencyInput
                  label="Valor do Crédito"
                  name="creditValue"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  initialValue={watch('creditValue')}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
              <Col md={3}>
                <CurrencyInput
                  label="Valor Entregue"
                  name="deliveredValue"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  initialValue={watch('deliveredValue')}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <CurrencyInput
                  label="Saldo"
                  name="balance"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  initialValue={watch('balance')}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
              <Col md={3}>
                <Controller
                  name="contractAt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing={true}
                      label="Data do Contrato"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                  rules={{
                    required: 'Data é obrigatória',
                  }}
                />
              </Col>
              <Col md={6}>
                <InputField
                  label="Observações"
                  name="observations"
                  placeholder="Digite as observações"
                  register={register}
                  errors={errors}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="personId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      onChange={(selectedPerson) => setValue('personId', selectedPerson)}
                      label="Cliente"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                      type={['isCustomer', 'isProducer', 'isSupplier']}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="productId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      onChange={(selectedProduct) => setValue('productId', selectedProduct)}
                      label="Produto"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <HarvestInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('harvestId', selectedHarvest)}
                      label="Safra"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FarmInput
                      value={field.value}
                      onChange={(selectedFarm) => setValue('farmId', selectedFarm)}
                      label="Fazenda"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SiloInput
                      value={field.value}
                      onChange={(selectedSilo) => setValue('siloId', selectedSilo)}
                      label="Silo"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditLotModal;